import { rootApi } from './root.api';

export const tenantApi = rootApi.injectEndpoints({
  tagTypes: ['Tenant'],
  endpoints: (builder) => ({
    getTenant: builder.query({
      query: () => ({
        url: 'tenant',
        method: 'GET',
      }),
      providesTags: ['Tenant'],
    }),
    checkForDuplicate: builder.query({
      query: ({ tenantId, testKey }) => ({
        url: `testkey/${tenantId}/${testKey}`,
        method: 'GET',
      }),
      providesTags: (result, error, { tenantId }) => [
        { type: 'Tenant', id: 'LIST' },
        { type: 'Tenant', id: tenantId },
      ],
    }),
    getTenantStats: builder.query({
      query: ({ period }) => ({
        url: `tenantstats/${period}`,
        method: 'GET',
        timeout: 10_000,
      }),
      providesTags: (result, error, { period }) => [
        { type: 'Tenant', id: 'LIST' },
        { type: 'Tenant', id: period },
      ],
      // Prevent refetching on mount/focus to reduce duplicate calls
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),
    updateTenant: builder.mutation({
      query: ({ tenant }) => ({
        url: 'tenant',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: tenant,
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateTenantGeneralInfo: builder.mutation({
      query: ({ tenantGeneralInfo }) => ({
        url: 'tenant/general-info',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: tenantGeneralInfo,
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateTenantIntegrationSettings: builder.mutation({
      query: ({ tenantIntegrationSettings }) => ({
        url: 'tenant/integration-settings',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: tenantIntegrationSettings,
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateTenantProjectSettings: builder.mutation({
      query: ({ tenantProjectSettings }) => ({
        url: 'tenant/project-settings',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: tenantProjectSettings,
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateTenantCustomFields: builder.mutation({
      query: ({ ctConfig, tenantId }) => ({
        url: 'tenant/custom-fields',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { ct_config: ctConfig, tenant_id: tenantId },
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateTenantThemeInfo: builder.mutation({
      query: ({ tenantThemeInfo }) => ({
        url: 'tenant/theme',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: tenantThemeInfo,
      }),
      invalidatesTags: ['Tenant'],
    }),
    activateTenant: builder.mutation({
      query: ({ tenant_id }) => ({
        url: 'tenant/activate',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { tenant_id },
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateTenantConnectionKey: builder.mutation({
      query: ({ key }) => ({
        url: 'connectionkey/',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { key },
      }),
      invalidatesTags: ['Tenant'],
    }),
    retrieveCTProjectTypes: builder.query({
      query: () => ({
        url: 'tenant/project-types',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: ['Tenant'],
    }),
    checkSupportedLanguagesMigration: builder.query({
      query: () => ({
        url: 'tenant/migration/supported-languages/check',
        method: 'GET',
      }),
      providesTags: ['Tenant'],
    }),
    executeSupportedLanguagesMigration: builder.mutation({
      query: () => ({
        url: 'tenant/migration/supported-languages/execute',
        method: 'POST',
      }),
      invalidatesTags: ['Tenant'],
    }),
  }),
});

export const {
  useGetTenantQuery,
  useLazyGetTenantQuery,
  useLazyCheckForDuplicateQuery,
  useLazyGetTenantStatsQuery,
  useUpdateTenantMutation,
  useUpdateTenantGeneralInfoMutation,
  useUpdateTenantIntegrationSettingsMutation,
  useUpdateTenantProjectSettingsMutation,
  useUpdateTenantCustomFieldsMutation,
  useUpdateTenantThemeInfoMutation,
  useActivateTenantMutation,
  useUpdateTenantConnectionKeyMutation,
  useRetrieveCTProjectTypesQuery,
  useCheckSupportedLanguagesMigrationQuery,
  useExecuteSupportedLanguagesMigrationMutation,
} = tenantApi;
